import { api } from '../base';
import { 
  BucketSchema, 
  CreateBucketRequestSchema,
  type Bucket, 
  type CreateBucketRequest 
} from '../types';
import { z } from 'zod';

export class BucketsApi {
  async getAll(): Promise<Bucket[]> {
    const response = await api.get('/v1/buckets');
    return z.array(BucketSchema).parse(response);
  }

  async getByProjectId(projectId: number): Promise<Bucket[]> {
    const buckets = await this.getAll();
    return buckets.filter(bucket => bucket.project_id === projectId);
  }

  async getById(id: number): Promise<Bucket> {
    const response = await api.get(`/v1/buckets/${id}`);
    return BucketSchema.parse(response);
  }

  async create(data: CreateBucketRequest): Promise<Bucket> {
    const validated = CreateBucketRequestSchema.parse(data);
    const response = await api.post('/v1/buckets', validated);
    return BucketSchema.parse(response);
  }

  async update(id: number, data: Partial<CreateBucketRequest>): Promise<Bucket> {
    const response = await api.put(`/v1/buckets/${id}`, data);
    return BucketSchema.parse(response);
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/v1/buckets/${id}`);
  }

  async getStats(bucketId: number): Promise<{
    fileCount: number;
    totalSize: string;
  }> {
    return api.get(`/v1/buckets/${bucketId}/stats`);
  }

  async getAccess(bucketId: number): Promise<Array<{
    user_id: number;
    email: string;
    avatar_url?: string;
    access_type: string;
    granted_at?: string;
    granted_by?: number;
  }>> {
    return api.get(`/v1/buckets/${bucketId}/access`);
  }

  async grantAccess(bucketId: number, userEmail: string): Promise<void> {
    await api.post(`/v1/buckets/${bucketId}/access`, { user_email: userEmail });
  }

  async revokeAccess(bucketId: number, userId: number): Promise<void> {
    await api.delete(`/v1/buckets/${bucketId}/access/${userId}`);
  }
}